import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MapPin, Briefcase, Building2 } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Separator } from '@/components/ui/Separator';
import { FadeIn } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { MarkdownRenderer } from '@/components/marketing/MarkdownRenderer';
import { useRoleBySlug } from '@/lib/cms';
import { formatDate } from '@/lib/utils';

export function RoleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: role, isLoading, isError } = useRoleBySlug(slug);

  if (isLoading) {
    return (
      <>
        <Meta title="Loading…" path={`/careers/${slug ?? ''}`} />
        <div className="pt-24 sm:pt-28 md:pt-32">
          <Skeleton className="h-[40vh] w-full" />
        </div>
      </>
    );
  }

  if (isError || !role) {
    return (
      <>
        <Meta title="Role not found" path={`/careers/${slug ?? ''}`} />
        <section className="flex min-h-[70vh] items-center justify-center pt-20">
          <div className="container-narrow text-center">
            <h1 className="display-serif text-display-l text-fg">Role not found.</h1>
            <p className="mt-3 text-fg-muted">It may have been closed or removed.</p>
            <div className="mt-6">
              <Button asChild>
                <Link to="/careers">
                  <ArrowLeft className="h-4 w-4" /> All roles
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Meta
        title={`${role.title} — Careers`}
        description={role.description_md?.slice(0, 200) ?? undefined}
        path={`/careers/${role.slug}`}
        jsonLd={
          {
            '@context': 'https://schema.org',
            '@type': 'JobPosting',
            title: role.title,
            description: role.description_md ?? undefined,
            datePosted: role.published_at ?? undefined,
            employmentType: role.type ?? undefined,
            hiringOrganization: {
              '@type': 'Organization',
              name: 'Vonlof',
              sameAs: 'https://vonlof.co',
            },
            jobLocation: {
              '@type': 'Place',
              address: { '@type': 'PostalAddress', addressLocality: role.location ?? undefined, addressCountry: 'GH' },
            },
          } as never
        }
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <Link
              to="/careers"
              className="inline-flex items-center gap-1 text-sm text-fg-muted transition-colors hover:text-gold-500"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All roles
            </Link>
          </FadeIn>
          <TextSplit
            text={role.title}
            className="mt-6 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.05}
          />
          <FadeIn delay={0.1}>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-fg-muted">
              {role.department && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" /> {role.department}
                </span>
              )}
              {role.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {role.location}
                </span>
              )}
              {role.type && (
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4" /> {role.type}
                </span>
              )}
              {role.published_at && <span>Posted {formatDate(role.published_at)}</span>}
            </div>
          </FadeIn>

          <div className="mt-12 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8 space-y-12">
              {role.description_md && (
                <FadeIn>
                  <MarkdownRenderer content={role.description_md} />
                </FadeIn>
              )}

              {role.responsibilities.length > 0 && (
                <FadeIn>
                  <h2 className="text-h3 text-fg">What you'll do</h2>
                  <ul className="mt-6 space-y-2">
                    {role.responsibilities.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-fg-muted">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}

              {role.requirements.length > 0 && (
                <FadeIn>
                  <h2 className="text-h3 text-fg">What you'll bring</h2>
                  <ul className="mt-6 space-y-2">
                    {role.requirements.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-fg-muted">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}

              {role.nice_to_haves.length > 0 && (
                <FadeIn>
                  <h2 className="text-h3 text-fg">Nice to have</h2>
                  <ul className="mt-6 space-y-2">
                    {role.nice_to_haves.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-fg-muted">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500/60" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <Card>
                  <CardHeader>
                    <CardTitle>Apply for this role</CardTitle>
                    <p className="mt-2 text-sm text-fg-muted">
                      Takes about 5 minutes. CV upload optional but encouraged.
                    </p>
                    <div className="mt-4">
                      <Button asChild size="lg" className="w-full">
                        <Link to={`/careers/${role.slug}/apply`}>
                          Apply now <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                    {role.apply_email && (
                      <>
                        <Separator className="my-4" />
                        <p className="text-xs text-fg-subtle">
                          Or email{' '}
                          <a className="text-gold-500 hover:underline" href={`mailto:${role.apply_email}`}>
                            {role.apply_email}
                          </a>
                          .
                        </p>
                      </>
                    )}
                  </CardHeader>
                </Card>
              </div>
            </aside>
          </div>
        </MaskReveal>
      </main>
    </>
  );
}